import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Keyboard, type TextInput } from 'react-native';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Input,
    KeyboardCompatibleBottomSheet,
    KeyboardCompatibleBottomSheetTextInput,
    YStack,
} from 'smoothie';

import { isEmpty } from '../../../../../common/misc';
import { SPACE_NAME_MAX_LENGTH } from '../../../../types/BankAccount';

interface EditNicknameSheetProps {
    onClose: () => void;
    onSubmit: (nickname: string) => void;
    nickname: string;
}

const EditNicknameSheet = ({
    onClose,
    onSubmit,
    nickname,
}: EditNicknameSheetProps) => {
    return (
        <KeyboardCompatibleBottomSheet
            onDismiss={onClose}
            title="Nickname"
            showCloseButton
        >
            {/* A bug with this sheet: Local state has to be managed in a separate component so the whole sheet doesn't re-render on state change (if it does, it doesn't account for the keyboard size) */}
            <EditNicknameInput onSubmit={onSubmit} nickname={nickname} />
        </KeyboardCompatibleBottomSheet>
    );
};

interface EditNicknameInputProps {
    onSubmit: (nickname: string) => void;
    nickname: string;
}

const EditNicknameInput = ({ onSubmit, nickname }: EditNicknameInputProps) => {
    const [rename, setRename] = useState(nickname);
    const inputRef = useRef<TextInput>(null);

    const isInvalid =
        isEmpty(rename.trim()) || rename.length > SPACE_NAME_MAX_LENGTH;

    // Fixes a bug where using autoFocus on KeyboardCompatibleBottomSheet results in an animation where the content is pushed out of view on mobile web. Adding a delay as a workaround keeps content visible
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 450);

        return () => {
            clearTimeout(timeoutId);
        };
    }, []);

    return (
        <YStack alignItems="center" marginTop={16}>
            <KeyboardCompatibleBottomSheetTextInput
                editable
                ref={inputRef}
                enablesReturnKeyAutomatically
                component={Input}
                value={rename}
                onChangeText={(text: string) => {
                    setRename(text);
                }}
                onSubmitEditing={() => {
                    Keyboard.dismiss();
                }}
                width="100%"
            />
            <BodyMediumRegular
                alignSelf="center"
                marginTop={4}
                marginBottom={24}
                color={isInvalid ? '$red1100' : '$gray1000'}
            >
                {rename.trim().length}/{SPACE_NAME_MAX_LENGTH}
            </BodyMediumRegular>
            <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.LARGE}
                text="Save"
                disabled={isInvalid}
                onPress={() => {
                    !isInvalid && onSubmit(rename);
                }}
            />
        </YStack>
    );
};

export default EditNicknameSheet;
